import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Star, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { apiClient, tokenStorage } from '../../utils/api-client';
import { locationImagesService } from '../../services/location-images';
import { useToast } from '../ui/use-toast';
import type { GalleryImage } from '../../types/location-images';

interface LocationImageCarouselProps {
  images: GalleryImage[];
  initialIndex: number;
  locationId: string;
  isStaffMember: boolean;
  onClose: () => void;
  onCoverSet: (coverImageUrl: string, imageId: string) => void;
  onImageAdded: (image: GalleryImage) => void;
  onImageDeleted: (imageId: string) => void;
}

export default function LocationImageCarousel({
  images: initialImages,
  initialIndex,
  locationId,
  isStaffMember,
  onClose,
  onCoverSet,
  onImageAdded,
  onImageDeleted,
}: LocationImageCarouselProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [images, setImages] = useState(initialImages);
  const [currentIndex, setCurrentIndex] = useState(
    Math.min(initialIndex, Math.max(0, initialImages.length - 1))
  );
  const [settingCover, setSettingCover] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const currentImage = images[currentIndex];

  const goToPrev = useCallback(() => {
    setCurrentIndex(i => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(i => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, goToPrev, goToNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) { goToNext(); } else { goToPrev(); }
    }
    touchStartX.current = null;
  };

  const handleSetAsCover = async () => {
    if (!currentImage) return;
    setSettingCover(true);
    try {
      const result = await locationImagesService.setAsCover(locationId, currentImage.id);
      onCoverSet(result.cover_image_url, currentImage.id);
      toast({ title: t('locationImages.coverSet') });
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setSettingCover(false);
    }
  };

  const handleDelete = async () => {
    if (!currentImage) return;
    setDeleting(true);
    try {
      await locationImagesService.deleteGalleryImage(locationId, currentImage.id);
      const newImages = images.filter(img => img.id !== currentImage.id);
      setImages(newImages);
      onImageDeleted(currentImage.id);
      if (newImages.length === 0) {
        onClose();
      } else {
        setCurrentIndex(i => Math.min(i, newImages.length - 1));
      }
      toast({ title: t('locationImages.imageDeleted') });
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const getApiOrigin = () => {
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
    try {
      const url = new URL(baseURL);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return { host: window.location.hostname, port: window.location.port || undefined, protocol: window.location.protocol.replace(':', '') };
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div className="flex justify-between items-center p-4 text-white">
        <span className="text-sm text-white/60">
          {images.length > 0 ? `${currentIndex + 1} / ${images.length}` : ''}
        </span>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10">
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images.length === 0 ? (
          <p className="text-white/60">{t('locationImages.noImages')}</p>
        ) : (
          <>
            {currentImage && (
              <img
                key={currentImage.id}
                src={currentImage.url}
                alt=""
                className="max-h-full max-w-full object-contain select-none"
                draggable={false}
              />
            )}

            {/* Prev / Next arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom action bar (staff only) */}
      {isStaffMember && (
        <div className="flex justify-between items-center p-4">
          {/* Left: Upload new photo */}
          <DirectUploadProvider
            origin={getApiOrigin()}
            headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
            onSuccess={async (signedIds: string[]) => {
              if (!signedIds[0]) return;
              try {
                const newImage = await locationImagesService.addGalleryImage(locationId, signedIds[0]);
                const newImages = [...images, newImage];
                setImages(newImages);
                setCurrentIndex(newImages.length - 1);
                onImageAdded(newImage);
                toast({ title: t('locationImages.imageAdded') });
              } catch {
                toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
              } finally {
                setUploadingImage(false);
              }
            }}
            render={({ handleUpload, ready }) => {
              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files.length > 0) {
                  setUploadingImage(true);
                  handleUpload(Array.from(e.target.files));
                }
                e.target.value = '';
              };
              return (
                <div className="flex gap-2">
                  <input type="file" accept="image/*" id="carousel-gallery-upload" className="hidden" onChange={handleChange} disabled={!ready || uploadingImage} />
                  <input type="file" accept="image/*" capture="environment" id="carousel-camera-upload" className="hidden" onChange={handleChange} disabled={!ready || uploadingImage} />
                  <label htmlFor="carousel-gallery-upload">
                    <Button type="button" variant="outline" size="sm" className="bg-black/40 border-white/20 text-white hover:bg-white/10 cursor-pointer" asChild disabled={!ready || uploadingImage}>
                      <span>
                        {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : t('locationImages.uploadPhoto')}
                      </span>
                    </Button>
                  </label>
                  <label htmlFor="carousel-camera-upload">
                    <Button type="button" variant="outline" size="sm" className="bg-black/40 border-white/20 text-white hover:bg-white/10 cursor-pointer sm:hidden" asChild disabled={!ready || uploadingImage}>
                      <span>{t('locationImages.takePhoto')}</span>
                    </Button>
                  </label>
                </div>
              );
            }}
          />

          {/* Right: Set as cover + Delete */}
          <div className="flex gap-2">
            {currentImage && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-black/40 border-white/20 text-red-400 hover:bg-red-900/30 hover:text-red-300"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
            {currentImage && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSetAsCover}
                disabled={settingCover}
                className="bg-black/40 border-white/20 text-white hover:bg-white/10"
              >
                {settingCover ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Star className="h-4 w-4 mr-2" />
                )}
                {t('locationImages.setAsCover')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Dot navigation */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentIndex ? 'bg-white scale-125' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
